import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate, useLocation, useOutletContext } from "react-router-dom"
import { Bounce, toast } from "react-toastify"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../../components/Common/EmptyState"
import { AvatarGroup } from "../../../components/DashboardComponents/AvatarGroup"
import {
  JoinShadowSessionModal,
  StartShadowSessionModal,
} from "../../../components/Modals"
import { ICONS } from "../../../constants/icons"
import CityHubTabHeader from "../CityHubTabHeader"
import {
  CityHubInfiniteSentinel,
  EventCardSkeleton,
  categoryClass,
} from "../CityHubCards"
import { CITY_HUB_INFINITE_PAGE_SIZE, PUBLIC_CITY_INFINITE_PAGE_SIZE, cityHubShadowEventPath, cityHubTabPath, unwrapCityHubPagedResult } from "../cityHubTabs"
import useInfinitePagination from "../../../hooks/useInfinitePagination"
import { get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { createCitySession, joinSession, toSessionCityPayload } from "../../../helpers/session"
import { getUserAvatarUrl } from "../../../helpers/userAvatar"
import { formatDate } from "../../../helpers/formatDate"
import { formatPersonName } from "../../CallsForArtist/callMappers"
import { formatWorkedHours } from "../../../helpers/sessionDetail"
import { useSessionDetailPlayer } from "../../../components/FloatingPlayer"
import { goToRequestInvite } from "../../PublicCity/publicCityPaths"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const SKELETON_COUNT = 4
const LOAD_MORE_SKELETON_COUNT = 2

const SESSION_LIST_WITH = ["creator", "collaborators.user", "targets"]

const SESSION_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "ended", label: "Ended" },
]

const SESSION_VISIBILITY_OPTIONS = [
  { value: "private", label: "Private" },
  { value: "global", label: "Public" },
]

const CREATOR_OPTIONS_PAGE_SIZE = 100

const normalizeSingleFilter = next =>
  Array.isArray(next) ? next[0] || "" : next || ""

const formatCreatorOptionName = user =>
  [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
  user?.full_name ||
  user?.name ||
  user?.email ||
  "Creator"

const extractMembersList = payload => {
  const result = payload?.data?.result
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.data)) return result.data
  return []
}

const LEFT_COLLAB_STATUSES = new Set(["left", "exited"])

const isUuid = value =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(value || "")
  )

const getSessionHostId = session =>
  session?.creator?.id ||
  session?.creator_id ||
  session?.user?.id ||
  session?.user_id ||
  null

const formatCollabStatus = status => {
  const s = String(status || "").toLowerCase()
  if (s === "joined" || s === "active" || s === "in session") return "In Session"
  if (s === "invited" || s === "pending") return "Invited"
  if (s === "left" || s === "exited") return "Exited"
  if (s === "paused" || s === "break") return "Paused"
  if (s === "host") return "In Session"
  return status ? String(status) : "In Session"
}

const isActiveCollabStatus = status => {
  const s = String(status || "").toLowerCase()
  return !LEFT_COLLAB_STATUSES.has(s) && s !== "exited"
}

const normalizeSessionStatus = value =>
  String(value || "")
    .toLowerCase()
    .trim()

const getSessionActionLabel = (collaborationStatus, sessionStatus) => {
  const status = normalizeSessionStatus(sessionStatus)
  const collabStatus = normalizeSessionStatus(collaborationStatus)

  if (status === "active" && (!collabStatus || collabStatus === "invited")) {
    return "Join Session"
  }
  if (status === "active" && collabStatus === "joined") {
    return "Session Joined"
  }
  return "View Detail"
}

const formatSessionListingDuration = (session, finished = false) => {
  const hours = Number(session?.total_working_hours)
  if (Number.isFinite(hours) && hours > 0) {
    return formatWorkedHours(hours)
  }
  return finished ? "Finished" : "Live"
}

const canJoinSessionFromListing = (session, { isCityMember = false } = {}) => {
  const status = normalizeSessionStatus(session?.status)
  const collabStatus = normalizeSessionStatus(
    session?.current_user_collaboration_status,
  )

  return (
    Boolean(isCityMember) &&
    status === "active" &&
    (!collabStatus || collabStatus === "invited")
  )
}

const mapSessionPeople = (session = {}, { includeLeft = true } = {}) => {
  const people = []
  const seen = new Set()

  const pushPerson = (user, extra = {}) => {
    if (!user || typeof user !== "object") return
    // Time entries / pivot rows have ids but no name fields — skip them.
    const hasUserIdentity =
      user.full_name ||
      user.first_name ||
      user.last_name ||
      user.name ||
      user.email ||
      user.profile_media
    if (!hasUserIdentity) return

    const id = user.id || extra.id
    if (!id || seen.has(String(id))) return
    seen.add(String(id))

    const formatted = formatPersonName(user)
    const name =
      formatted && formatted !== "N/A"
        ? formatted
        : extra.name || "Member"

    people.push({
      id,
      name,
      avatar: getUserAvatarUrl(user),
      src: getUserAvatarUrl(user),
      role: extra.role || user?.role || "",
      status: formatCollabStatus(extra.status),
    })
  }

  // Only people on the session collaborators list (not time-entry rows).
  ;(session.collaborators || []).forEach(collaborator => {
    if (!includeLeft && !isActiveCollabStatus(collaborator?.status)) return
    pushPerson(collaborator?.user, {
      role: collaborator?.role,
      status: collaborator?.status,
    })
  })

  // If API omitted the host from collaborators, still show creator once.
  if (people.length === 0 && session.creator) {
    pushPerson(session.creator, { status: "joined" })
  }

  return people
}

const mapApiSessionToEvent = (
  session,
  cityName = "City Hub",
  isCityMember = false,
) => {
  const hostUser = session?.creator || session?.user || null
  const hostId = getSessionHostId(session)
  const started = session?.started_at || session?.created_at
  const sessionStatus = normalizeSessionStatus(session?.status)
  const collaborationStatus = normalizeSessionStatus(
    session?.current_user_collaboration_status,
  )
  const finished =
    Boolean(session?.ended_at) ||
    sessionStatus === "ended" ||
    sessionStatus === "finished" ||
    sessionStatus === "completed"
  const people = mapSessionPeople(session)
  const joinCollaborators = mapSessionPeople(session, { includeLeft: false })
  const typeLabel = session?.type
    ? String(session.type).charAt(0).toUpperCase() +
      String(session.type).slice(1)
    : "Session"
  const canJoin = canJoinSessionFromListing(session, { isCityMember })
  const actionLabel = getSessionActionLabel(collaborationStatus, sessionStatus)

  const workingNote = String(session?.working_note || "").trim()
  const apiTitle = String(session?.title || "").trim()
  const displayTitle = apiTitle || workingNote || "Shadow Session"
  const displaySubtitle =
    workingNote && workingNote !== displayTitle ? workingNote : ""

  return {
    id: session?.id,
    title: displayTitle,
    subtitle: displaySubtitle,
    project: cityName,
    host: formatPersonName(hostUser) || "—",
    hostId,
    category: typeLabel,
    datetime: formatDate(started, { format: "MMM D, h:mm A", fallback: "—" }),
    duration: formatSessionListingDuration(session, finished),
    attendees: people.length,
    canJoin,
    actionLabel,
    collaborationStatus,
    sessionStatus,
    visibility:
      String(session?.visibility_type || "global").toLowerCase() === "private"
        ? "Private"
        : "Public",
    startedAt: formatDate(started, {
      format: "MMMM D, YYYY, h:mm A",
      fallback: "—",
    }),
    startedBy: {
      name: formatPersonName(hostUser) || "—",
      avatar: getUserAvatarUrl(hostUser),
    },
    sessionNote: workingNote || apiTitle || "",
    collaborators: people,
    joinCollaborators,
    raw: session,
  }
}

const mergeSessionPages = (prev, next) => {
  const seen = new Set(prev.map(item => String(item.id)))
  return [
    ...prev,
    ...next.filter(item => item?.id && !seen.has(String(item.id))),
  ]
}

const CityHubEventsTab = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const currentUserId = useSelector(state => state.auth.currentUser?.id) || null
  const {
    tab,
    city,
    yourCities = [],
    isCityLoading = false,
    isCityMember = false,
    isPublic = false,
    listScrollRootRef,
  } = useOutletContext()
  const {
    openSessionDetail,
    isOpen,
    sessionId: floatingSessionId,
    minimize,
  } = useSessionDetailPlayer()
  const cityId = city?.id || null
  const cityName = city?.name || city?.shortName || "City Hub"

  const openInvite = useCallback(() => {
    goToRequestInvite(
      navigate,
      {
        from: "public-city",
        cityId,
        tab: "shadow-working-events",
        city,
      },
      { isAuthenticated: Boolean(currentUserId) },
    )
  }, [city, cityId, currentUserId, navigate])

  const shadowEventsReturnTo = useMemo(
    () => ({
      returnTo: cityHubTabPath("shadow-working-events", cityId),
      returnLabel: "Returning to Shadow Working Events...",
    }),
    [cityId],
  )

  const openCityShadowSession = useCallback(
    ({ sessionId, sessionCity } = {}) => {
      if (!sessionId) return

      const clickedId = String(sessionId)
      const activeFloatingId = floatingSessionId ? String(floatingSessionId) : ""

      if (isOpen && activeFloatingId && clickedId !== activeFloatingId) {
        minimize?.()
        navigate(
          cityHubShadowEventPath(
            sessionCity?.id || city?.id,
            sessionId,
          ),
        )
        return
      }

      openSessionDetail({
        sessionId,
        ...toSessionCityPayload(sessionCity || city),
        ...shadowEventsReturnTo,
      })
    },
    [
      city,
      floatingSessionId,
      isOpen,
      minimize,
      navigate,
      openSessionDetail,
      shadowEventsReturnTo,
    ],
  )

  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [visibilityFilter, setVisibilityFilter] = useState("")
  const [creatorId, setCreatorId] = useState("")
  const [creatorOptions, setCreatorOptions] = useState([])
  const [sessions, setSessions] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [isStartOpen, setIsStartOpen] = useState(false)
  const [joiningEvent, setJoiningEvent] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const sentinelRef = useRef(null)
  const pageRef = useRef(1)
  const loadInFlightRef = useRef(false)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    const targetSessionId = location.state?.openJoinSessionId
    if (!targetSessionId || !cityId || isLoading || isCityLoading) return undefined

    let cancelled = false

    const clearJoinNavigationState = () => {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      })
    }

    const openJoinModalForSession = async () => {
      let event = sessions.find(
        item => String(item.id) === String(targetSessionId),
      )

      if (!event) {
        try {
          const response = await get(endpoints.getSessionById(targetSessionId), {
            params: { "with[]": SESSION_LIST_WITH },
            skipErrorToast: true,
          })
          if (response?.data?.success) {
            event = mapApiSessionToEvent(
              response.data.result,
              cityName,
              isCityMember,
            )
          }
        } catch {
          event = null
        }
      }

      if (cancelled) return

      if (!event?.canJoin) {
        clearJoinNavigationState()
        return
      }

      setJoiningEvent(event)
      clearJoinNavigationState()
    }

    openJoinModalForSession()

    return () => {
      cancelled = true
    }
  }, [
    location.state?.openJoinSessionId,
    location.pathname,
    location.search,
    sessions,
    isLoading,
    isCityLoading,
    cityId,
    cityName,
    isCityMember,
    navigate,
  ])

  useEffect(() => {
    let cancelled = false

    const loadCreators = async () => {
      // Creator filter options API is member-only — skip on public city pages.
      if (!cityId || isPublic) {
        setCreatorOptions([])
        setCreatorId("")
        return
      }

      setCreatorId("")

      try {
        const response = await get(endpoints.getCityMembers(cityId), {
          params: {
            page: 1,
            per_page: CREATOR_OPTIONS_PAGE_SIZE,
            search: "",
            sort_by: "total_xp",
            sort_direction: "desc",
          },
          skipErrorToast: true,
        })
        if (cancelled) return

        if (!response?.data?.success) {
          setCreatorOptions([])
          return
        }

        const options = extractMembersList(response)
          .map(user => {
            const id = user?.id
            if (!id) return null
            return {
              value: String(id),
              label: formatCreatorOptionName(user),
            }
          })
          .filter(Boolean)

        setCreatorOptions(options)
      } catch {
        if (!cancelled) setCreatorOptions([])
      }
    }

    loadCreators()
    return () => {
      cancelled = true
    }
  }, [cityId, isPublic])

  const fetchSessions = useCallback(
    async ({ page: pageToLoad = 1, append = false, silent = false } = {}) => {
      if (!cityId) {
        setSessions([])
        setHasMore(false)
        setError(null)
        setIsLoading(false)
        setIsLoadingMore(false)
        return
      }

      if (append) setIsLoadingMore(true)
      else if (!silent) {
        setIsLoading(true)
        setError(null)
      }

      try {
        const pageSize = isPublic
          ? PUBLIC_CITY_INFINITE_PAGE_SIZE
          : CITY_HUB_INFINITE_PAGE_SIZE
        const params = {
          "city_ids[]": cityId,
          page: pageToLoad,
          page_size: pageSize,
          per_page: pageSize,
          sort_by: "started_at",
          sort_direction: "desc",
          "with[]": SESSION_LIST_WITH,
        }
        if (debouncedSearch) params.search = debouncedSearch
        if (statusFilter) params.status = statusFilter
        if (visibilityFilter) params.visibility_type = visibilityFilter
        if (creatorId) params["creator_ids[]"] = creatorId

        const response = await get(
          isPublic ? endpoints.getPublicSessions : endpoints.getSessions,
          { params },
        )

        if (!response?.data?.success) {
          const message = toastApiError(response, "Failed to load sessions")
          setError(message)
          if (!append) setSessions([])
          setHasMore(false)
          return
        }

        const { list, currentPage, hasMore: nextHasMore } =
          unwrapCityHubPagedResult(response, {
            pageToLoad,
            pageSize,
          })
        const mapped = list.map(item =>
          mapApiSessionToEvent(item, cityName, isCityMember),
        )

        setSessions(prev => (append ? mergeSessionPages(prev, mapped) : mapped))
        pageRef.current = currentPage
        setHasMore(nextHasMore)
      } catch (err) {
        const message = toastApiError(err, "Failed to load sessions")
        setError(message)
        if (!append) setSessions([])
        setHasMore(false)
      } finally {
        loadInFlightRef.current = false
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    [
      cityId,
      cityName,
      debouncedSearch,
      statusFilter,
      visibilityFilter,
      creatorId,
      isCityMember,
      isPublic,
    ]
  )

  useEffect(() => {
    pageRef.current = 1
    fetchSessions({ page: 1, append: false })
  }, [fetchSessions])

  const fetchSessionsRef = useRef(fetchSessions)
  fetchSessionsRef.current = fetchSessions

  useEffect(() => {
    const handleSessionLeft = event => {
      const {
        sessionId: leftSessionId,
        totalWorkingHours,
        sessionStatus,
        collaborationStatus,
      } = event?.detail || {}

      if (leftSessionId) {
        setSessions(prev =>
          prev.map(item => {
            if (String(item.id) !== String(leftSessionId)) return item

            const nextCollab = collaborationStatus || "left"
            const nextStatus = normalizeSessionStatus(
              sessionStatus || item.sessionStatus || "active",
            )
            const raw = {
              ...(item.raw || {}),
              current_user_collaboration_status: nextCollab,
              ...(totalWorkingHours != null
                ? { total_working_hours: totalWorkingHours }
                : {}),
              ...(sessionStatus ? { status: sessionStatus } : {}),
            }
            const finished =
              Boolean(raw?.ended_at) ||
              nextStatus === "ended" ||
              nextStatus === "finished" ||
              nextStatus === "completed"

            return {
              ...item,
              collaborationStatus: nextCollab,
              sessionStatus: nextStatus,
              canJoin: false,
              actionLabel: getSessionActionLabel(nextCollab, nextStatus),
              duration: formatSessionListingDuration(raw, finished),
              raw,
            }
          }),
        )
      }

      fetchSessionsRef.current({ page: 1, append: false, silent: true })
    }

    window.addEventListener("session-left", handleSessionLeft)
    return () => window.removeEventListener("session-left", handleSessionLeft)
  }, [])

  const loadMore = useCallback(() => {
    if (
      !hasMore ||
      isLoading ||
      isLoadingMore ||
      loadInFlightRef.current
    ) {
      return
    }
    loadInFlightRef.current = true
    fetchSessions({ page: pageRef.current + 1, append: true })
  }, [fetchSessions, hasMore, isLoading, isLoadingMore])

  useInfinitePagination({
    hasMore: hasMore && !isLoading,
    isLoading: isLoadingMore,
    onLoadMore: loadMore,
    resetKey: sessions.length,
    sentinelRef,
    rootRef: listScrollRootRef,
  })

  const filterConfig = useMemo(() => {
    const filters = [
      {
        key: "status",
        type: "select",
        label: "Status",
        multiple: false,
        options: SESSION_STATUS_OPTIONS,
        value: statusFilter,
        onChange: next => {
          setStatusFilter(normalizeSingleFilter(next))
        },
      },
      {
        key: "visibility_type",
        type: "select",
        label: "Visibility",
        multiple: false,
        options: SESSION_VISIBILITY_OPTIONS,
        value: visibilityFilter,
        onChange: next => {
          setVisibilityFilter(normalizeSingleFilter(next))
        },
      },
    ]

    // Creator filter is member-only (options API is not public).
    if (!isPublic) {
      filters.push({
        key: "creator_ids",
        type: "select",
        label: "Creator",
        multiple: false,
        searchable: true,
        options: creatorOptions,
        value: creatorId,
        defaultValue: "",
        onChange: next => {
          setCreatorId(normalizeSingleFilter(next))
        },
      })
    }

    return filters
  }, [
    statusFilter,
    visibilityFilter,
    creatorId,
    creatorOptions,
    isPublic,
  ])

  const handleStartSession = useCallback(
    async ({ title, note, cityId: nextCityId, userIds = [], visibility = "global" }) => {
      if (isSubmitting) return
      if (!isCityMember) {
        toast.error(
          "Only members of this city can start shadow sessions.",
          TOAST_OPTIONS,
        )
        return
      }
      setIsSubmitting(true)
      try {
        const result = await createCitySession({
          title,
          note,
          cityId: nextCityId,
          userIds,
          visibility,
        })
        if (!result.ok) {
          if (result.reason === "empty_city") {
            toast.error("Please select a city.", TOAST_OPTIONS)
          } else if (result.reason === "empty_note") {
            toast.error(
              "Please add a working note before starting the session.",
              TOAST_OPTIONS
            )
          } else if (result.reason === "empty_collaborators") {
            toast.error(
              "Select at least one collaborator for a private session.",
              TOAST_OPTIONS
            )
          } else if (result.response) {
            toastApiError(result.response, "Failed to create session")
          }
          return
        }

        toast.success(
          result.response?.data?.message || "Shadow session created.",
          TOAST_OPTIONS
        )
        setIsStartOpen(false)
        await fetchSessions({ page: 1, append: false })
        const createdId = result.created?.id
        if (createdId) {
          const sessionCity =
            yourCities.find(item => String(item.id) === String(nextCityId)) ||
            (city && String(city.id) === String(nextCityId) ? city : null) ||
            { id: nextCityId, name: city?.name || city?.shortName || "" }
          openCityShadowSession({
            sessionId: createdId,
            sessionCity,
          })
        }
      } catch (err) {
        toastApiError(err, "Failed to create session")
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      fetchSessions,
      isSubmitting,
      city,
      yourCities,
      openCityShadowSession,
      isCityMember,
    ]
  )

  const handleJoinSession = useCallback(
    async ({ note }) => {
      if (isSubmitting || !joiningEvent?.id) return
      if (!isCityMember) {
        toast.error(
          "Only members of this city can join shadow sessions.",
          TOAST_OPTIONS,
        )
        setJoiningEvent(null)
        return
      }
      if (!isUuid(joiningEvent.id)) {
        toast.error("This session can’t be joined yet.", TOAST_OPTIONS)
        return
      }

      setIsSubmitting(true)
      try {
        const result = await joinSession(joiningEvent.id, { note })
        if (!result.ok) {
          if (result.response) {
            toastApiError(result.response, "Failed to join session")
          }
          return
        }
        toast.success(
          result.response?.data?.message || "Joined shadow session.",
          TOAST_OPTIONS
        )
        setJoiningEvent(null)
        await fetchSessions({ page: 1, append: false })
        openCityShadowSession({
          sessionId: joiningEvent.id,
          sessionCity: city,
        })
      } catch (err) {
        toastApiError(err, "Failed to join session")
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      isSubmitting,
      joiningEvent,
      city,
      openCityShadowSession,
      fetchSessions,
      isCityMember,
    ]
  )

  const showInitialSkeleton = isCityLoading || isLoading
  const canStartSession = Boolean(cityId && isCityMember && !isPublic)
  const showPublicCta = Boolean(isPublic && cityId)
  const headerCtaLabel = showPublicCta
    ? tab.ctaLabel
    : canStartSession
      ? tab.ctaLabel
      : null

  return (
    <>
      <CityHubTabHeader
        title={tab.pageTitle}
        subtitle={tab.pageSubtitle}
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
        searchPlaceholder={tab.searchPlaceholder}
        filterConfig={filterConfig}
        showFilter={tab.showFilter}
        ctaLabel={headerCtaLabel}
        ctaIconSrc={
          (canStartSession || showPublicCta) &&
          tab.ctaIcon &&
          ICONS[tab.ctaIcon]
            ? ICONS[tab.ctaIcon]
            : undefined
        }
        onCtaClick={() => {
          if (showPublicCta) {
            openInvite()
            return
          }
          if (!canStartSession) return
          setIsStartOpen(true)
        }}
      />

      <div
        className="city-hub__events-grid"
        aria-busy={showInitialSkeleton || isLoadingMore || undefined}
      >
        {showInitialSkeleton
          ? Array.from({ length: SKELETON_COUNT }).map((_, index) => (
              <EventCardSkeleton key={`city-event-skeleton-${index}`} />
            ))
          : null}

        {!showInitialSkeleton &&
          sessions.map(event => {
            const avatars = (event.collaborators || []).map(person => ({
              name: person.name,
              src: person.avatar || person.src,
            }))
            const openEvent = () =>
              openCityShadowSession({
                sessionId: event.id,
                sessionCity: city,
              })
            const canJoin = Boolean(event.canJoin) && !isPublic
            const openJoin = () => setJoiningEvent(event)
            const onCardActivate = () => {
              if (isPublic) {
                openInvite()
                return
              }
              if (canJoin) {
                openJoin()
                return
              }
              openEvent()
            }

            return (
              <article
                key={event.id}
                className="city-hub__event-card"
                onClick={onCardActivate}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    onCardActivate()
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div className="city-hub__event-heading">
                  <span className="city-hub__event-project">{event.project}</span>
                  <span className={categoryClass(event.category)}>
                    {event.category}
                  </span>
                  {event.visibility ? (
                    <span
                      className={`city-hub__event-visibility city-hub__event-visibility--${
                        String(event.visibility).toLowerCase() === "private"
                          ? "private"
                          : "public"
                      }`}
                    >
                      {event.visibility}
                    </span>
                  ) : null}
                </div>

                <h3 className="city-hub__event-title">{event.title}</h3>
                {event.subtitle ? (
                  <p className="city-hub__event-subtitle">{event.subtitle}</p>
                ) : null}

                <div className="city-hub__event-meta">
                  <div className="city-hub__event-host">
                    <span>Host:</span>
                    <strong>{event.host}</strong>
                  </div>
                  <div className="city-hub__event-time">
                    <span>{event.datetime}</span>
                    <span className="city-hub__event-dot" aria-hidden="true">
                      ·
                    </span>
                    <span>{event.duration}</span>
                  </div>
                </div>

                <div className="city-hub__event-footer">
                  <AvatarGroup
                    people={avatars}
                    size="xs"
                    max={3}
                    total={event.attendees}
                    className="city-hub__event-avatars"
                  />
                  {canJoin ? (
                    <CustomButton
                      variant="primary"
                      size="sm"
                      className="city-hub__event-cta"
                      trailingIconSrc={ICONS.arrowRightBlack}
                      trailingIconAlt=""
                      onClick={e => {
                        e.stopPropagation()
                        openJoin()
                      }}
                    >
                      {event.actionLabel}
                    </CustomButton>
                  ) : (
                    <CustomButton
                      variant="secondary"
                      size="sm"
                      className="city-hub__event-cta"
                      trailingIconSrc={ICONS.arrowRightBlack}
                      trailingIconAlt=""
                      onClick={e => {
                        e.stopPropagation()
                        if (isPublic) {
                          openInvite()
                          return
                        }
                        openEvent()
                      }}
                    >
                      {isPublic ? "Request Invite" : event.actionLabel}
                    </CustomButton>
                  )}
                </div>
              </article>
            )
          })}

        {!showInitialSkeleton && isLoadingMore
          ? Array.from({ length: LOAD_MORE_SKELETON_COUNT }).map((_, index) => (
              <EventCardSkeleton key={`city-event-more-skeleton-${index}`} />
            ))
          : null}

        {!showInitialSkeleton && !isLoadingMore && sessions.length === 0 ? (
          <EmptyState
            title={error ? "Unable to load sessions" : "No events found"}
            description={
              error ||
              (!cityId
                ? "Select a city in the banner to browse shadow sessions."
                : isPublic
                  ? "Nothing matches this search or filter yet. Request an invite to join this city."
                  : !isCityMember
                    ? "Only members of this city can start sessions. You can still browse as a visitor."
                    : "Nothing matches this search or filter yet.")
            }
            actionText={
              showPublicCta
                ? tab.ctaLabel
                : canStartSession
                  ? "Start Session"
                  : undefined
            }
            onActionClick={
              showPublicCta
                ? openInvite
                : canStartSession
                  ? () => setIsStartOpen(true)
                  : undefined
            }
          />
        ) : null}
      </div>
      <CityHubInfiniteSentinel
        sentinelRef={sentinelRef}
        hasMore={hasMore && !isLoading}
      />

      {!isPublic ? (
        <>
          <StartShadowSessionModal
            isOpen={isStartOpen && canStartSession}
            onClose={() => !isSubmitting && setIsStartOpen(false)}
            variant="city"
            cities={city?.id ? [city] : []}
            selectedCity={city}
            onStart={handleStartSession}
            isSubmitting={isSubmitting}
          />

          <JoinShadowSessionModal
            isOpen={Boolean(joiningEvent)}
            onClose={() => !isSubmitting && setJoiningEvent(null)}
            variant="city"
            city={city}
            collaborators={
              joiningEvent?.joinCollaborators ||
              joiningEvent?.collaborators ||
              []
            }
            onJoin={handleJoinSession}
            isSubmitting={isSubmitting}
          />
        </>
      ) : null}
    </>
  )
}

export default CityHubEventsTab
